import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {HeroBannerText} from './hero-banner-text'
import {remCalc} from '../../../../../plugins/px-to-rem-babel-custom-plugin/utils/remCalc'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {calculateVerticalPadding, getMediaHeight, getHerobannerGradient} from '../utils'
import {getDamImagePicture} from '../../../custom-hooks/use-dam-image'
import {Helmet} from 'react-helmet'

export const HeroBanner = ({
    isMaskHeaderActive = true,
    textBlock,
    variant = 'white', // theme can either be black or white
    height = 'big',
    maxWidth = remCalc(1920),
    hasPaddings = true,
    buttonText,
    countdown,
    titleAs,
    isFirstElement = false,
    ...props
}) => {
    const imageSetKey = `image_${height}`
    const imageSetObj = props?.[imageSetKey]
    const isSmallHeight = height === 'small'
    const isMediumHeight = height === 'mid'
    const styles = useStyleConfig('HeroBanner')

    const direction =
        textBlock?.tabletMobileAlignment === 'bottom-center' ? 'column' : 'column-reverse'
    const verticalPadding = calculateVerticalPadding(direction, hasPaddings)

    const isVideo = props?.videos?.desktop && props?.videos?.mobile

    const gradient = getHerobannerGradient(
        textBlock.desktopAlignment,
        props?.maskgroup?.opacity,
        isMaskHeaderActive,
    )

    const mediaHeight = getMediaHeight(height)
    const isHeightAuto = mediaHeight === 'auto'

    const videoObjectFit = useBreakpointValue(
        {
            lg: 'cover',
        },
        {fallback: 'lg'},
    )

    return (
        <Flex
            data-testid={`hero-banner-container-${direction}`}
            flexDir={direction}
            pb={verticalPadding}
            maxW={maxWidth}
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
            {...styles.heroBannerContainer}
        >
            <Flex
                data-testid={
                    isVideo
                        ? 'hero-banner-video'
                        : imageSetObj
                        ? 'hero-banner-image'
                        : 'hero-banner-text'
                }
                pt={isHeightAuto ? {base: '86%', md: '35%', lg: '10%'} : undefined}
                pb={isHeightAuto ? {lg: '10%'} : undefined}
                maxW={maxWidth}
                h={isVideo ? {lg: mediaHeight} : undefined}
                {...styles.heroBannerMain}
            >
                {!isVideo && (
                    <Box position="relative" width="100%">
                        {getDamImagePicture(
                            imageSetObj,
                            textBlock?.title || 'hero-banner-image',
                            isFirstElement,
                        )}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            width="100%"
                            height="100%"
                            zIndex={1}
                            bgImage={{base: 'none', lg: gradient}}
                            pointerEvents="none"
                        />
                    </Box>
                )}

                {isVideo && (
                    <>
                        {isFirstElement && (
                            <Helmet>
                                <link
                                    rel="preload"
                                    as="fetch"
                                    href={props?.videos?.desktop}
                                    // eslint-disable-next-line react/no-unknown-property
                                    fetchpriority="high"
                                    type="video/mp4"
                                ></link>
                                <link
                                    rel="preload"
                                    as="fetch"
                                    href={props?.videos?.mobile}
                                    // eslint-disable-next-line react/no-unknown-property
                                    fetchpriority="high"
                                    type="video/mp4"
                                ></link>
                            </Helmet>
                        )}
                        <Box
                            {...styles.heroBannerVideo}
                            _after={{
                                background: {lg: gradient ? `${gradient}` : undefined},
                            }}
                        >
                            <video
                                playsInline
                                autoPlay
                                muted
                                loop
                                aria-hidden="true"
                                style={{
                                    height: '100%',
                                    width: '100%',
                                    objectFit: videoObjectFit,
                                }}
                                preload="auto"
                                // eslint-disable-next-line react/no-unknown-property
                                fetchpriority="high"
                            >
                                <source
                                    src={props?.videos?.desktop}
                                    type="video/mp4"
                                    media="(min-width: 1280px)"
                                />
                                <source
                                    src={props?.videos?.mobile}
                                    type="video/mp4"
                                    media="(max-width: 1279px)"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    </>
                )}
                <HideOnMobile {...styles.heroBannerDesktop}>
                    <HeroBannerText
                        variant={variant}
                        countdown={countdown}
                        textBlock={textBlock}
                        buttonText={buttonText}
                        isSmallHeight={isSmallHeight}
                        isMediumHeight={isMediumHeight}
                        titleAs={titleAs}
                    />
                </HideOnMobile>
            </Flex>
            <HideOnDesktop {...styles.heroBannerMobile}>
                <HeroBannerText
                    variant={variant}
                    maxWidth={maxWidth}
                    countdown={countdown}
                    textBlock={textBlock}
                    hasPaddings={hasPaddings}
                    buttonText={buttonText}
                    titleAs={titleAs}
                />
            </HideOnDesktop>
        </Flex>
    )
}

HeroBanner.propTypes = {
    isMaskHeaderActive: PropTypes.bool,
    titleAs: PropTypes.string,
    textBlock: PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
        bodyCopy: PropTypes.string,
        desktopAlignment: PropTypes.string,
        tabletMobileAlignment: PropTypes.string,
    }),
    buttonText: PropTypes.object,
    variant: PropTypes.string,
    height: PropTypes.string,
    link: PropTypes.string,
    maxWidth: PropTypes.string,
    hasPaddings: PropTypes.bool,
    countdown: PropTypes.array,
    maskgroup: PropTypes.shape({
        opacity: PropTypes.string,
    }),
    videos: PropTypes.shape({
        desktop: PropTypes.string,
        mobile: PropTypes.string,
    }),
    isFirstElement: PropTypes.bool,
}
